import { api, ApiError } from './api';
import type { AudioItem, AudioKind } from './types';

/**
 * Audio endpoints and presentation helpers, mirroring lib/clients.ts.
 * The upload helper is XHR because fetch cannot report upload progress;
 * it lives here (not api.ts) so the shared wrapper stays JSON-only.
 */

export interface AudioUpdateInput {
  title?: string;
  description?: string | null;
  kind?: AudioKind;
  published?: boolean;
  durationSec?: number | null;
  wooProductId?: string | null;
  storeUrl?: string | null;
}

export const audioApi = {
  list: (clientId?: string) =>
    api.get<{ files: AudioItem[] }>(clientId ? `/audio?clientId=${encodeURIComponent(clientId)}` : '/audio'),
  update: (id: string, body: AudioUpdateInput) => api.patch<{ file: AudioItem }>(`/audio/${id}`, body),
  del: (id: string) => api.del(`/audio/${id}`),
  grant: (id: string, userId: string) => api.post(`/audio/${id}/grant`, { userId }),
  revoke: (id: string, userId: string) => api.del(`/audio/${id}/grant/${userId}`),
};

export function streamUrl(id: string): string {
  return `/api/audio/${id}/stream`;
}

export function downloadUrl(id: string): string {
  return `/api/audio/${id}/download`;
}

export interface UploadFields {
  title: string;
  kind: AudioKind;
  description?: string;
  durationSec?: string;
  wooProductId?: string;
  storeUrl?: string;
  published?: 'true' | 'false';
}

/** XHR upload with progress + abort. Resolves with the created file. */
export function uploadAudio(
  fields: UploadFields,
  file: File,
  onProgress: (loaded: number, total: number) => void,
): { promise: Promise<{ file: AudioItem }>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<{ file: AudioItem }>((resolve, reject) => {
    const form = new FormData();
    // Fields first, then the file — the server collects fields as they stream.
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined && value !== '') form.append(key, value);
    }
    form.append('file', file);

    xhr.open('POST', '/api/audio');
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      try {
        const body = JSON.parse(xhr.responseText) as { file?: AudioItem; error?: string };
        if (xhr.status >= 200 && xhr.status < 300 && body.file) resolve({ file: body.file });
        else reject(new ApiError(xhr.status, body.error ?? 'Upload failed'));
      } catch {
        reject(new ApiError(xhr.status, 'Upload failed'));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, 'Upload failed'));
    xhr.onabort = () => reject(new ApiError(0, 'Upload cancelled'));
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 'B';
  for (const next of units) {
    if (value < 1024) break;
    value /= 1024;
    unit = next;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${unit}`;
}

/** Seconds-based duration (sessions' formatDuration takes an ISO pair — separate on purpose). */
export function formatAudioDuration(sec: number | null): string {
  if (sec === null || !Number.isFinite(sec) || sec <= 0) return '—';
  const minutes = Math.round(sec / 60);
  if (minutes < 1) return '<1 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Lucide names as plain strings — lib never imports from design. */
export function audioKindIcon(kind: AudioKind): string {
  return kind === 'ultrasonic' ? 'waves' : 'headphones';
}
